import React, {useEffect, useRef, useState, useContext} from 'react';
import {View, ScrollView, Platform, InputAccessoryView, Keyboard, TouchableOpacity} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import moment from 'moment';

import Header from '../../components/elements/Header';
import Text from '../../components/elements/Text';
import Divider from '../../components/elements/Divider';
import Notes from '../../components/modules/Notes';
import Button from '../../components/elements/Button';
import ButtonWithIcon from '../../components/elements/ButtonWithIcon';
import NotesCalendar from '../../components/modules/NotesCalendar';
import TimePicker from '../../components/elements/TimePicker';
import Popup from '../../components/elements/Popup';
import Input from '../../components/elements/Input';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {BUTTON_TYPES} from '../../utils/Constants';
import {DLog} from '../../utils/HelperMethods';
import {getPatientCondition} from '../../utils/GeneralStorage';
import {useTimeFormat, formatTime} from '../../hooks/useTimeFormat';
import {fetchApi} from '../../utils/ApiHelper';
import {BLOOD_SUGAR_LEVEL, HTTP_METHODS} from '../../utils/URLConstants';
import {syncReadingsToHealthKit} from '../../healthkit/HealthKitSync';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import CommonStyles from '../../utils/CommonStyles';
import {tID} from '../../utils/TestIdHelper';
import {hapticSuccess, hapticError} from '../../utils/Haptics';
import {
  CloseIcon,
  DateIcon,
  LeftArrowIcon,
  TimeIcon,
} from '../../utils/SvgIcons';

export const ReadingExistsModal = ({
  duplicateModalVisibility = false,
  errorFromBackend = {},
  onPressReplace = () => {},
  setDuplicateModalVisibility = () => {},
  timeFormat = '24',
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <Popup
      testID={tID('AddGlucoseLevel-Popup-ReadingExists')}
      visible={duplicateModalVisibility}
      title="Reading already exists"
      description={
        <>
          You already have a blood sugar reading for the time selected. Would
          you like to replace your reading of{' '}
          <Text
            testID={tID('AddGlucoseLevel-Text-BloodSugarCount')}
            variation="Body1SemiBold"
            color={colors.textMuted}>
            {errorFromBackend?.record?.bloodSugarCount}
          </Text>{' '}
          for{' '}
          <Text
            testID={tID('AddGlucoseLevel-Text-Date1')}
            variation="Body1SemiBold"
            color={colors.textMuted}>
            {new Date(
              errorFromBackend?.record?.patientConditionMeasurement?.measureAt,
            )
              .toDateString()
              .substring(4, 15)}
          </Text>{' '}
          at{' '}
          <Text
            testID={tID('AddGlucoseLevel-Text-Date2')}
            variation="Body1SemiBold"
            color={colors.textMuted}>
            {formatTime(
              errorFromBackend?.record?.patientConditionMeasurement?.measureAt,
              timeFormat
            )}
          </Text>
          ?
        </>
      }>
      <Button
        testID={tID('AddGlucoseLevel-Button-Replace')}
        style={styles.leftButton}
        onPress={onPressReplace}
        title={'Replace'}
      />
      <Button
        testID={tID('AddGlucoseLevel-Button-PopupCancel')}
        titleColor={colors.textOnAccent}
        buttonType={BUTTON_TYPES.Filled}
        style={styles.button}
        onPress={() => setDuplicateModalVisibility(false)}
        title={'Cancel'}
      />
    </Popup>
  );
};

const AddGlucoseLevel = ({navigation, route, onClose = () => {}}) => {
  // DLog('AddGlucoseLevel props', route?.params);
  const extraData = route?.params?.extraData;
  const getLevel = () => {
    return extraData?.diabetesMeasurement?.bloodSugarCount
      ? extraData?.diabetesMeasurement?.bloodSugarCount
      : 0;
  };
  const getNotes = () => {
    return extraData?.heading
      ? [
          {
            title: extraData?.heading,
            noteText: extraData?.patientNote,
            createAt: extraData?.measureAt,
          },
        ]
      : [];
  };
  const getReadingDate = () => {
    return extraData?.measureAt ? new Date(extraData?.measureAt) : undefined;
  };
  const getReadingTime = () => {
    return extraData?.measureAt
      ? moment(new Date(extraData?.measureAt)).format('LT')
      : null;
  };

  const getEditMode = () => {
    return extraData ? true : false;
  };

  const editMode = getEditMode();
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const timeFormat = useTimeFormat();
  const [level, setLevel] = useState(getLevel());
  const [isSetRange, setIsSetRange] = useState(false);
  const [readingDate, setReadingDate] = useState(getReadingDate());
  const [readingTime, setReadingTime] = useState(getReadingTime());
  const [visibleDatePicker, setVisibleDatePicker] = useState(false);
  const [visibleTimePicker, setVisibleTimePicker] = useState(false);
  const [validTime, setValidTime] = useState(true);
  const [notes, setNotes] = useState(getNotes());
  const [duplicateModalVisibility, setDuplicateModalVisibility] =
    useState(false);
  const [errorFromBackend, setErrorFromBackend] = useState({});

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);

  const hyperValue = useRef(null);
  const hypoValue = useRef(null);
  const conditionID = useRef(null);
  const statusText = useRef('Low');

  useEffect(() => {
    if (Platform.OS === 'web') window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    async function getCondition() {
      const patientCondition = await getPatientCondition();
      if (patientCondition) {
        if (patientCondition.minValue && patientCondition.maxValue) {
          hyperValue.current = patientCondition.minValue;
          hypoValue.current = patientCondition.maxValue;
          setIsSetRange(true);
        } else {
          hyperValue.current = 20;
          hypoValue.current = 600;
          setIsSetRange(false);
        }
        if (patientCondition.condition) {
          conditionID.current = patientCondition.condition.id;
        }
      } else {
        hyperValue.current = 20;
        hypoValue.current = 600;
        setIsSetRange(false);
      }
    }

    let unsubscribe;

    if (Platform.OS !== 'web') {
      unsubscribe = navigation?.addListener('focus', () => {
        getCondition();
      });
    } else {
      getCondition();
    }

    return unsubscribe;
  }, [navigation]);

  useEffect(() => {
    let measureTime = new Date().toISOString();
    let dateString = moment().format('YYYY-MM-DD');
    if (readingDate) {
      dateString = readingDate;
    }

    if (readingTime) {
      dateString = dateString + ' ' + readingTime;
    } else {
      dateString = dateString + ' ' + new Date().toTimeString();
    }
    const date = moment(dateString, 'YYYY-MM-DD HH:mm').toDate();
    measureTime = new Date(date.getTime());
    if (measureTime.getTime() > new Date().getTime()) {
      setValidTime(false);
    } else {
      setValidTime(true);
    }
  }, [readingDate, readingTime]);

  const updateData = () => {
    const params = {
      id: parseInt(extraData.id),
      bloodSugarCount: parseInt(level),
    };
    if (level > 0) {
      params.bloodSugarCount = parseInt(level);
    }
    if (notes.length > 0) {
      params.heading = notes[0].title;
      params.patientNote = notes[0].noteText;
    }
    DLog('updateData params', params);
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.PUT, BLOOD_SUGAR_LEVEL, params)
      .then(res => {
        DLog('response updateData', res.data);
        lpc.setVisible(false);
        if (res.status == 200) {
          hapticSuccess();
          redirectBack();
        }
      })
      .catch(e => {
        lpc.setVisible(false);
        hapticError();
        esc.showError(e);
      });
  };

  const onPressSave = () => {
    if (editMode) {
      updateData();
    } else {
      let measureTime = new Date().toISOString();
      let dateString = moment().format('YYYY-MM-DD');
      if (readingDate) {
        dateString = readingDate;
      }

      if (readingTime) {
        dateString = dateString + ' ' + readingTime;
      } else {
        dateString = dateString + ' ' + new Date().toTimeString();
      }

      const date = moment(dateString, 'YYYY-MM-DD HH:mm').toDate();
      measureTime = date.toISOString();

      const params = {
        bloodSugarCount: parseInt(level),
        measureAt: measureTime,
      };

      if (conditionID.current) {
        params['conditionId'] = parseInt(conditionID.current);
      }
      if (notes.length > 0) {
        params['patientNote'] = notes[0].noteText;
        params['heading'] = notes[0].title;
      }

      lpc.setVisible(true);
      fetchApi(HTTP_METHODS.POST, BLOOD_SUGAR_LEVEL, params)
        .then(res => {
          //DLog(res);
          lpc.setVisible(false);
          if (res.status == 200) {
          // Mirror the new manual reading into Apple Health (no-op unless
          // the user opted in; never blocks the save flow) — #65
          syncReadingsToHealthKit([
            {
              glucoseValueMgdl: params.bloodSugarCount,
              measuredAtIso: params.measureAt,
              source: 'manual',
            },
          ]);
          hapticSuccess();
          redirectBack();
        }
        })
        .catch(e => {
          lpc.setVisible(false);
          DLog('the error addglucoseLevel', e);
          //add two numbers
          if (e && e.message == 'Duplicate data.') {
            setDuplicateModalVisibility(true);
            setErrorFromBackend({...e});
          } else {
            hapticError();
        esc.showError(e);
          }
        });
    }
  };

  const onPressReplace = () => {
    setDuplicateModalVisibility(false);
    DLog('onPressReplace', errorFromBackend);
    let params = {
      bloodSugarCount: parseInt(level),
      id: parseInt(errorFromBackend?.record?.patientConditionMeasurement?.id),
    };
    if (notes.length > 0) {
      params.heading = notes[0].title;
      params.patientNote = notes[0].noteText;
    }
    DLog('onPressReplace', params);
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.PUT, BLOOD_SUGAR_LEVEL, params)
      .then(res => {
        // DLog(res.data);
        lpc.setVisible(false);
        if (res.status == 200) {
          hapticSuccess();
          redirectBack();
        }
      })
      .catch(e => {
        lpc.setVisible(false);
        hapticError();
        esc.showError(e);
      });
  };

  const onChangeReadingNotes = notes => {
    setNotes(notes);
  };

  const onCalendarClose = selectDate => {
    if (selectDate) {
      setReadingDate(selectDate);
    }
    setVisibleDatePicker(false);
  };

  const onTimePickerClose = time => {
    if (time) {
      setReadingTime(time);
    }
    setVisibleTimePicker(false);
  };

  if (isSetRange) {
    if (level == 0) {
      statusText.current = '-';
    } else if (level > 0 && level < hyperValue.current) {
      statusText.current = 'Low';
    } else if (level > hyperValue.current && level < hypoValue.current) {
      statusText.current = 'In Range';
    } else if (level > hypoValue.current) {
      statusText.current = 'High';
    }
  }

  const renderGlucoseLevelText = () => {
    if (level > 0 && level < 20) {
      return (
        <Text
          testID={tID('AddGlucoseLevel-Text-LO')}
          variation="HeadlineBold"
          color={colors.textPrimary}
          textAlign={'center'}>
          LO
        </Text>
      );
    } else if (level > 600) {
      return (
        <Text
          testID={tID('AddGlucoseLevel-Text-HI')}
          variation="HeadlineBold"
          color={colors.textPrimary}
          textAlign={'center'}>
          HI
        </Text>
      );
    }
    // In-range values are already shown in the input above — no duplicate readout.
    return null;
  };

  const renderDateTime = () => {
    const isToday = moment().diff(readingDate, 'days') == 0;
    return (
      <>
        <Text variation="Subtitle1SemiBold" color={colors.textPrimary}>
          Set Date and Time
        </Text>
        <View style={styles.dateTimeContainer}>
          <ButtonWithIcon
            testID={tID('AddGlucoseLevel-ButtonWithIcon-Date')}
            style={styles.buttonLeft}
            title={isToday ? 'Today' : moment(readingDate).format('MM/DD/YYYY')}
            icon={<DateIcon />}
            titleColor={colors.textPrimary}
            onPress={() => setVisibleDatePicker(true)}
            disabled={editMode}
          />
          <ButtonWithIcon
            testID={tID('AddGlucoseLevel-ButtonWithIcon-Time')}
            style={styles.buttonRight}
            title={readingTime ? readingTime : 'Now'}
            icon={<TimeIcon />}
            titleColor={colors.textPrimary}
            onPress={() => setVisibleTimePicker(true)}
            disabled={editMode}
          />
        </View>
      </>
    );
  };

  const redirectBack = () => {
    if (route?.params?.onGoBack) route?.params?.onGoBack();
    if (Platform.OS === 'web') {
      onClose();
    } else {
      navigation.goBack();
    }
  };

  const onChangeInputText = val => {
    const parsedVal = Number.parseInt(val);
    if (Number.isNaN(parsedVal) || parsedVal <= 0) {
      setLevel(0);
    } else if (parsedVal > 620) {
      setLevel(620);
    } else {
      setLevel(Math.ceil(parsedVal));
    }
  };

  return (
    <SafeAreaView
      testID={tID('AddGlucoseLevel-SafeAreaView-Main')}
      style={[
        styles.container,
        Platform.OS === 'web' && CommonStyles.full_screen_height,
      ]}>
      <View
        testID={tID('AddGlucoseLevel-View-HeaderContainer')}
        style={styles.headerView}>
        <Header
          title="Manual Entry"
          onPressLeftIcon={redirectBack}
          leftImage={Platform.OS === 'web' ? <CloseIcon /> : <LeftArrowIcon />}
        />
      </View>
      <ScrollView keyboardShouldPersistTaps="handled"
        testID={tID('AddGlucoseLevel-ScrollView-1')}
        style={styles.container}>
        <View style={styles.content}>
          <View style={styles.glucoseInputContainer}>
            <Input
              testID={tID('AddGlucoseLevel-Input-Glucose')}
              maxLength={3}
              value={level === 0 ? '' : level.toString()}
              onChangeText={onChangeInputText}
              keyboardType={'number-pad'}
              placeholder="0"
              inputStyle={styles.glucoseInput}
              style={styles.glucoseInputWrapper}
              inputAccessoryViewID="glucoseInputAccessory"
            />
            <Text variation="Body1Regular" color={colors.textMuted} style={styles.glucoseUnit}>
              mg/dL
            </Text>
          </View>
          {Platform.OS === 'ios' && (
            <InputAccessoryView nativeID="glucoseInputAccessory">
              <View style={styles.keyboardAccessory}>
                <TouchableOpacity onPress={() => Keyboard.dismiss()} style={styles.doneButton}>
                  <Text variation="Body1SemiBold" color={colors.accentStrong}>Done</Text>
                </TouchableOpacity>
              </View>
            </InputAccessoryView>
          )}
          {(level > 0 && level < 20) || level > 600 ? (
            <View style={styles.mt24}>{renderGlucoseLevelText()}</View>
          ) : null}
          <Text
            testID={tID('AddGlucoseLevel-Text-DiabetesStatus')}
            variation="Text1SemiBold"
            color={colors.textMuted}
            topMargin={32}>
            {isSetRange ? 'Result' : ''}
          </Text>
          <Text
            testID={tID('AddGlucoseLevel-Text-Status')}
            variation="Subtitle1Bold"
            color={colors.textPrimary}
            topMargin={8}>
            {isSetRange ? statusText.current : ''}
          </Text>

          <View style={styles.dateTime}>{renderDateTime()}</View>
          <Divider style={styles.fullWidth} top={35} />
        </View>
        <Notes
          navigation={navigation}
          notes={notes}
          onChangeNotes={onChangeReadingNotes}
          hideAddButton={notes.length > 0}
          showDateTime={false}
        />
      </ScrollView>
      <View style={[styles.buttonsContainer, {paddingBottom: 16}]}>
        <Button
          testID={tID('AddGlucoseLevel-Button-Cancel')}
          style={styles.button}
          title={'Cancel'}
          onPress={redirectBack}
        />
        <Button
          testID={tID('AddGlucoseLevel-Button-Save')}
          disabled={(!editMode && level == 0) || !validTime}
          style={[styles.button, styles.ml16]}
          title={'Save'}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
          onPress={onPressSave}
        />
      </View>
      <NotesCalendar
        selectedDate={readingDate && moment(readingDate).format('YYYY-MM-DD')}
        visible={visibleDatePicker}
        onClose={onCalendarClose}
      />
      <TimePicker visible={visibleTimePicker} onClose={onTimePickerClose} />
      <ReadingExistsModal
        duplicateModalVisibility={duplicateModalVisibility}
        errorFromBackend={errorFromBackend}
        onPressReplace={onPressReplace}
        setDuplicateModalVisibility={setDuplicateModalVisibility}
        timeFormat={timeFormat}
      />
    </SafeAreaView>
  );
};

export default AddGlucoseLevel;
